import { useEffect, useRef, useState } from 'react';
import { number } from 'prop-types';
import { trim } from 'lodash';

import GlobalSearchView from './GlobalSearchView';
import ResultWindow from './ResultWindow';
import { GlobalSearchStoreConsumer, GlobalSearchStoreProvider } from './UseGlobalSearchStore';
import useOnClickOutside from '../../utilities/UseOnClickOutside';
import useKeyListener, { KEYS } from '../../utilities/UseKeyListener';

export default function GlobalSearch(props) {
    const { width } = props;
    const [isExpanded, setIsExpanded] = useState(false);
    const [isActive, setIsActive] = useState(false);

    const viewRef = useRef();
    const windowRef = useRef();

    const showResultWindow = isExpanded && isActive;

    // hide everything if we click outside view/window
    useOnClickOutside([viewRef, windowRef], () => setIsActive(false));

    // hide window if we click a link
    useAnchorMouseDown(windowRef, () => setIsActive(false));

    // hide search if we hit escape
    useKeyListener(KEYS.ESCAPE, () => setIsExpanded(false));

    return (
        <GlobalSearchStoreProvider>
            <GlobalSearchView
                isExpanded={isExpanded}
                isActive={isActive}
                toggleIsExpanded={() => setIsExpanded(!isExpanded)}
                activate={() => setIsActive(true)}
                ref={viewRef}
                width={width}
            />

            <GlobalSearchStoreConsumer>
                {({ search }) => (
                    <ResultWindow
                        ref={windowRef}
                        width={width}
                        show={showResultWindow && trim(search).length > 0}
                        alignmentRef={viewRef}
                    />
                )}
            </GlobalSearchStoreConsumer>
        </GlobalSearchStoreProvider>
    );
}

GlobalSearch.propTypes = {
    width: number,
};

GlobalSearch.defaultProps = {
    width: 500,
};

function useAnchorMouseDown(targetRef, callback) {
    const targetEl = targetRef.current;
    const callbackRef = useRef();
    callbackRef.current = callback;

    useEffect(() => {
        if (!targetEl) return;

        targetEl.addEventListener('mousedown', onMouseDown);

        return () => targetEl.removeEventListener('mousedown', onMouseDown);

        function onMouseDown(e) {
            let parent = e.target;

            while (parent) {
                if (parent && typeof parent.matches === 'function' && parent.matches('a')) {
                    callbackRef.current(e);
                    return;
                }

                parent = parent.parentNode;
            }
        }
    }, [targetEl]);
}
